/**
 * The standup — what was promised, and what actually happened.
 */

import type { RouteManifestEntry } from '../route-manifest.js';

export const INSIGHT_ROUTES: readonly RouteManifestEntry[] = [
  /*
   * The index, and one route per report beneath it — M-26, change order 06 §4.
   *
   * **The index carries the only nav item.** Six reports in the sidebar would make INSIGHTS the
   * largest section in the product and would put the navigation back to changing shape every time
   * a capability lands, which is exactly what M-25 took it out of. The next three reports cost a
   * card on the index and nothing here.
   *
   * The index is gated on `insight` while the reports it lists are gated individually, because one
   * of them is not served by `insight` at all — time to fill is a `clients` route, and the reason
   * is on the route file. A tenant with `clients` and without `insight` reaches that report by
   * URL; the index is where its card would have been, and it is gone with the section.
   */
  {
    id: 'insights',
    path: '/insights',
    entitlement: 'insight',
    requires: { action: 'read', resource: 'application' },
    authenticated: true,
    nav: { labelKey: 'insights.nav', section: 'insights', order: 1 },
    load: () => import('../screens/insights/index.js'),
  },
  {
    id: 'insights-funnel',
    path: '/insights/funnel',
    entitlement: 'insight',
    requires: { action: 'read', resource: 'application' },
    authenticated: true,
    load: () => import('../screens/insights/funnel.js'),
  },
  {
    id: 'insights-conversion',
    path: '/insights/conversion',
    entitlement: 'insight',
    requires: { action: 'read', resource: 'application' },
    authenticated: true,
    load: () => import('../screens/insights/conversion.js'),
  },
  {
    /*
     * Where the days go — CO9 §10.2. Two readings on one screen, because they are two halves of
     * one fact: open waits have a count, finished ones have a length, and a reader holding one in
     * their head while looking at the other is a reader who conflates them.
     */
    id: 'insights-stalls',
    path: '/insights/stalls',
    entitlement: 'insight',
    requires: { action: 'read', resource: 'application' },
    authenticated: true,
    load: () => import('../screens/insights/stalls.js'),
  },
  {
    /* The same spans, read at a client rather than about a desk — hence its own screen. */
    id: 'insights-client-responsiveness',
    path: '/insights/client-responsiveness',
    entitlement: 'insight',
    requires: { action: 'read', resource: 'application' },
    authenticated: true,
    load: () =>
      import('../screens/insights/stalls.js').then((module) => ({
        default: module.ClientResponsivenessReport,
      })),
  },
  {
    id: 'insights-job-health',
    path: '/insights/job-health',
    entitlement: 'insight',
    requires: { action: 'read', resource: 'application' },
    authenticated: true,
    load: () => import('../screens/insights/job-health.js'),
  },
  {
    id: 'insights-activity',
    path: '/insights/activity',
    entitlement: 'insight',
    requires: { action: 'read', resource: 'application' },
    authenticated: true,
    load: () => import('../screens/insights/activity.js'),
  },
  {
    id: 'insights-effort',
    path: '/insights/effort',
    entitlement: 'insight',
    // The effort figures are about interviews, which is the permission the per-job report asks
    // for. The same question at a different cardinality is the same permission.
    requires: { action: 'read', resource: 'interview' },
    authenticated: true,
    load: () => import('../screens/insights/effort.js'),
  },
  {
    id: 'insights-time-to-fill',
    path: '/insights/time-to-fill',
    // `clients`, not `insight` — both facts this report needs live in tables that module owns.
    // See `modules/clients/src/routes/time-to-fill.ts` for the argument.
    entitlement: 'clients',
    requires: { action: 'read', resource: 'job' },
    authenticated: true,
    load: () => import('../screens/insights/time-to-fill.js'),
  },
  // Gated on `insight`, not on `pipeline`: the standup is scored from a projection `insight`
  // owns, and an agency that has not bought the analytics module has nothing to count against.
  {
    id: 'standup',
    path: '/huddle',
    entitlement: 'insight',
    requires: { action: 'read', resource: 'commitment' },
    authenticated: true,
    nav: { labelKey: 'huddle.nav', section: 'insights', order: 2 },
    load: () => import('../screens/standup.js'),
  },
  /*
   * The standup's other two views — spec §24.1's *"the four views"*, of which three exist.
   *
   * **Neither carries a nav entry, and that is the point.** They are three readings of one morning
   * reached from a switcher at the top of the board, not three places in the product. The digest
   * used to be a sidebar item; dropping it takes INSIGHTS from three entries to two, which is the
   * same argument the reports index makes above — the sidebar must not grow once per capability.
   *
   * My day renders the component Home already embeds, so a recruiter's day has one definition.
   */
  {
    id: 'standup-my-day',
    path: '/huddle/my-day',
    entitlement: 'insight',
    requires: { action: 'read', resource: 'commitment' },
    authenticated: true,
    load: () => import('../screens/standup/my-day.js'),
  },
  {
    id: 'standup-digest',
    path: '/huddle/digest',
    entitlement: 'insight',
    requires: { action: 'read', resource: 'application' },
    authenticated: true,
    load: () => import('../screens/standup/digest.js'),
  },
  /*
   * Two of §7.2's sections, on the job record but gated here.
   *
   * Activity and Commitments are both `insight` projections — the event stream and the standup — so
   * an agency without that module sees neither tab rather than two empty ones. Same reasoning as
   * `job-pipeline` in the pipeline manifest: the tab lives where its entitlement is true.
   */
  {
    id: 'job-activity',
    path: '/jobs/$jobId/activity',
    entitlement: 'insight',
    authenticated: true,
    load: () => import('../screens/job-detail.js'),
  },
  {
    id: 'job-commitments',
    path: '/jobs/$jobId/commitments',
    entitlement: 'insight',
    authenticated: true,
    load: () => import('../screens/job-detail.js'),
  },
];
